import { useTranslation } from 'react-i18next';
import { LogosCarousel } from '@/components/logos-carousel';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import type { Sponsor } from '../../types/sponsor';

/**
 * The sponsors of a trophy or a competition, as a thin strip of logos under the
 * cover (issue #374). On a stage it took the place of the link back to the
 * circuit, which the NavBar's breadcrumb already gives.
 *
 * Logos only, about 32px high: the name is the image's alt text and tooltip.
 * Three columns on a phone, five from `sm` up; with no more logos than columns
 * nothing moves. The moving strip is hidden from assistive technology and
 * repeated as a static list (WCAG 2.2.2) that only screen readers read and
 * that shows itself while a keyboard is on one of its links. Renders nothing at
 * all when there are no sponsors.
 */
export default function SponsorsStrip({ sponsors }: { sponsors: Sponsor[] }) {
  const { t } = useTranslation();
  const wide = useMediaQuery('(min-width: 640px)');

  const shown = sponsors.filter((sponsor) => sponsor.logo_small_url || sponsor.logo_url);
  if (shown.length === 0) return null;

  return (
    <section aria-label={t('sponsors.stripLabel')} data-testid="sponsors-strip">
      <ul className="sr-only focus-within:not-sr-only focus-within:flex focus-within:flex-wrap focus-within:gap-3 focus-within:text-xs">
        {shown.map((sponsor) => (
          <li key={sponsor.id}>
            {sponsor.website_url ? (
              <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer sponsored">
                {sponsor.name}
              </a>
            ) : (
              sponsor.name
            )}
          </li>
        ))}
      </ul>
      <div aria-hidden="true">
        <LogosCarousel columnCount={wide ? 5 : 3} className="h-10 gap-x-4">
          {shown.map((sponsor) => (
            <SponsorLogo key={sponsor.id} sponsor={sponsor} />
          ))}
        </LogosCarousel>
      </div>
    </section>
  );
}

function SponsorLogo({ sponsor }: { sponsor: Sponsor }) {
  const image = (
    <img
      src={sponsor.logo_small_url ?? sponsor.logo_url ?? undefined}
      alt={sponsor.name}
      title={sponsor.name}
      loading="lazy"
      className="h-8 w-auto max-w-full object-contain"
    />
  );

  if (!sponsor.website_url) return image;

  // Out of the tab order, as the strip is hidden from assistive technology: the
  // static list above is the one a keyboard reaches.
  return (
    <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer sponsored" tabIndex={-1}>
      {image}
    </a>
  );
}
